import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

import { taskboard } from "../../services/taskboardService";
import { detectLinkCategory, linkTypeMeta } from "../../constants/taskboardLinks";

// ──────────────────────────────────────────────────────────────────────
// AddLinkModal — attach or edit an external link (product rule 7). The
// type is AUTO-DETECTED from the URL on paste (video / document /
// scout_report / other) and can be overridden; the title is optional
// (falls back to the URL). Categories match the backend enum exactly.
// ──────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: "video", label: "Video" },
  { value: "document", label: "Drive doc" },
  { value: "scout_report", label: "Report" },
  { value: "other", label: "Generic link" },
];

const AddLinkModal = ({ isOpen, onClose, folderId, link = null, onSuccess }) => {
  const isEdit = Boolean(link);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setUrl(link.url || "");
      setTitle(link.title || "");
      setDescription(link.description || "");
      setCategory(link.category || detectLinkCategory(link.url || ""));
      setCategoryTouched(true); // don't override an existing link's category
    } else {
      setUrl("");
      setTitle("");
      setDescription("");
      setCategory("other");
      setCategoryTouched(false);
    }
  }, [isOpen, isEdit, link]);

  // Auto-detect the type from the URL until the user picks one manually.
  const onUrlChange = (value) => {
    setUrl(value);
    if (!categoryTouched) setCategory(detectLinkCategory(value));
  };

  const meta = linkTypeMeta(category);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("URL is required");
      return;
    }
    setSubmitting(true);
    const payload = {
      url: url.trim(),
      title: title.trim() || url.trim(),
      description: description.trim(),
      category,
    };
    const response = isEdit
      ? await taskboard.updateLink(folderId, link.id, payload)
      : await taskboard.createLink(folderId, payload);
    setSubmitting(false);
    if (!response.success) {
      toast.error(response.error || "Failed to save link");
      return;
    }
    toast.success(isEdit ? "Link updated" : "Link added");
    onSuccess && onSuccess(response.data);
    onClose && onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h2 className="text-xl font-semibold text-[#0B0B0B]">{isEdit ? "Edit link" : "Add link"}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">URL *</label>
            <input
              type="url"
              value={url}
              onChange={(e) => onUrlChange(e.target.value)}
              placeholder="Paste a Drive doc, video, report or any URL…"
              required
              className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
            {url.trim() && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[12px]">
                <span className="font-semibold px-1.5 py-px rounded-full" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                <span className="text-[#98A2B3]">detected, change below if needed</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Defaults to the URL"
              maxLength={200}
              className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this link about?"
              rows={2}
              className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Type</label>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setCategoryTouched(true); }}
              className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 border border-[#E9E9E9] rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-[#2283FF] text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Add link"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default AddLinkModal;
